import 'dayjs/locale/en-au';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

/**
 * Init dayjs base on language code.
 *
 * @author Ritchie Zhu <fengfu.zhu@paid.inc>
 * @date 2023-04-20
 * @param language - Language code.
 */
export function initDayjs(language = 'en-AU') {
  dayjs.locale(language.toLowerCase());
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isToday);
  dayjs.extend(isYesterday);
  dayjs.extend(localeData);
  dayjs.extend(relativeTime);
  dayjs.extend(utc);
  dayjs.extend(weekday);
  dayjs.extend(timezone);
  dayjs.extend(customParseFormat);
  dayjs.localeData();
  dayjs.tz.setDefault('Australia/Melbourne');
}

/**
 * Format UTC date to Melbourne local date time
 *
 * @param date - UTC date string
 * @returns Melbourne locale date
 */
export function utc2Mel(date?: string | number | dayjs.Dayjs | Date | null) {
  return dayjs(date).tz('Australia/Melbourne');
}

/**
 * Format UTC date to Melbourne local date time (string)
 *
 * @param date - UTC date string
 * @param format - Date format, default: YYYY-MM-DD
 * @returns Melbourne locale date
 */
export function utc2MelStr(
  date?: string | number | dayjs.Dayjs | Date | null,
  format = 'YYYY-MM-DD',
) {
  return utc2Mel(date).format(format);
}

/**
 * Melbourne now
 *
 * @returns Melbourne now date
 */
export function nowMel() {
  return dayjs().tz('Australia/Melbourne');
}

/**
 * Get the current Australian financial year label, e.g. "25/26".
 *
 * The Australian financial year runs from 1 July to 30 June, so any date
 * in July or later belongs to a financial year starting in the same
 * calendar year; earlier dates belong to the previous calendar year's.
 *
 * @param date - Reference date, defaults to Melbourne now
 * @returns Two-digit financial year label, e.g. "25/26"
 */
export function getCurrentFinancialYearLabel(date = nowMel()) {
  // dayjs months are 0-indexed, so July is 6.
  const startYear = date.month() >= 6 ? date.year() : date.year() - 1;
  const pad = (year: number) => `${year % 100}`.padStart(2, '0');
  return `${pad(startYear)}/${pad(startYear + 1)}`;
}

/**
 * Get deadline in days from submitted by date.
 *
 * @param submittedBy - Submitted by date in month
 * @returns Deadline in days
 */
export function getDeadlineInDays(submittedBy = 28) {
  const now = nowMel();
  const day = now.date();
  if (submittedBy >= day) {
    return submittedBy - day;
  } else {
    const year = now.year();
    const month = now.month();
    const today = dayjs(`${year}-${month}-${day}`);
    const submitted = dayjs(`${year}-${month + 1}-${submittedBy}`);
    return submitted.diff(today, 'day');
  }
}

/**
 * Get next claim month
 *
 * @param submittedBy - Submitted by date in month
 * @returns Get next claim month
 */
export function getNextClaimMonth(submittedBy = 28) {
  const now = nowMel();
  const day = now.date();
  const year = now.year();
  const month = now.month();
  return submittedBy >= day ? now.format('MMMM') : now.add(1, 'M').format('MMMM');
}

/**
 * Format a UTC date to a Melbourne day-only string, or '-' when missing.
 *
 * @param date - UTC date string
 * @returns Formatted date or '-'
 */
export function formatDayOnly(date?: string | null) {
  return date ? utc2Mel(date).format('DD MMM YYYY') : '-';
}

/**
 * Format a UTC date to a Melbourne day and time, down to the second, or '-' when
 * missing. Seconds matter where two entries can land in the same minute — an audit
 * or check history, say — so the caller is choosing precision over brevity.
 *
 * @param date - UTC date string
 * @returns Formatted date and time, or '-'
 */
export function formatDayAndTime(date?: string | null) {
  return date ? utc2Mel(date).format('DD MMM YYYY, HH:mm:ss') : '-';
}

/**
 * Format a UTC date to a Melbourne time-only string, or '-' when missing. For a column
 * that already carries the day on its own line and would only repeat it.
 *
 * @param date - UTC date string
 * @returns Formatted time, or '-'
 */
export function formatTimeOnly(date?: string | null) {
  return date ? utc2Mel(date).format('HH:mm:ss') : '-';
}

/**
 * Format a work period as "<start> - <end>", or '-' when both ends are missing.
 *
 * @param startDate - UTC start date string
 * @param endDate - UTC end date string
 * @returns Formatted work period range
 */
export function formatWorkPeriod(startDate?: string | null, endDate?: string | null) {
  if (!startDate && !endDate) return '-';
  return `${formatDayOnly(startDate)} - ${formatDayOnly(endDate)}`;
}

/**
 * Format a UTC date as "DD MMM YYYY · <relative days>" in Melbourne time, or '-' when missing.
 *
 * @param date - UTC date string
 * @returns Formatted date with relative-day suffix, or '-'
 */
export function formatDateWithRelativeDays(date?: string | null) {
  if (!date) return '-';
  const day = utc2Mel(date).startOf('day');
  const days = day.diff(nowMel().startOf('day'), 'days');
  return `${day.format('DD MMM YYYY')} · ${formatRelativeDays(days)}`;
}

/**
 * Format a relative day count as a human-readable string.
 *
 * @param days - Number of days relative to today (negative = past, 0 = today, positive = future)
 * @returns "N days ago", "Today", or "in N days"
 */
export function formatRelativeDays(days: number) {
  if (days === 0) return 'Today';
  if (days === -1) return 'Yesterday';
  if (days === 1) return 'Tomorrow';
  if (days < 0) return `${Math.abs(days)} days ago`;
  return `in ${days} days`;
}
